/**
 * This page lists all jobs within a project.
 */
import * as Tabs from '@wandb/weave/components/Tabs';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';

import {
  useFetchProjectInternalIdQuery,
  useFetchProjectJobsQuery,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {createProjectIdFilter} from '../filterUtil';
import {
  useFetchProjectJobsIncrementally,
  usePublicQueueAccess,
  useRecentlyUsedJobs,
} from '../hooks';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {
  WANDB_ENTITY_NAME,
  WANDB_JOBS_PROJECT_NAME,
} from '../publicQueue/constants';
import {InfoBanner} from '../Queues/InfoBanner';
import {JobsHeader} from './JobsHeader';
import {JobsTable} from './JobsTable';
import {getJobInfo, getPublicJobInfo, getRecentlyUsedJobInfo} from './jobUtil';
import {PublicJobCard} from './PublicJobCard';
import {ZeroJobs} from './ZeroJobs';

export enum JobsPageTab {
  PROJECT = 'project',
  PUBLIC = 'public',
  RECENTLY_USED = 'recently used',
}

export const JOBS_PAGE_TAB_LABELS = {
  [JobsPageTab.PROJECT]: 'Project jobs',
  [JobsPageTab.PUBLIC]: 'W&B public jobs',
  [JobsPageTab.RECENTLY_USED]: 'Recently used jobs',
} as const;

interface JobsPageProps {
  entityName: string;
  projectName: string;
}

export const JobsPage: React.FC<JobsPageProps> = ({
  entityName,
  projectName,
}) => {
  const [jobToRun, setJobToRun] = useState<{
    jobId: string;
    jobEntityName: string;
    jobProjectName: string;
  } | null>(null);
  const [currentTab, setCurrentTab] = useState<JobsPageTab>();

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const projectInternalIdQuery = useFetchProjectInternalIdQuery({
    variables: {
      entityName,
      projectName,
    },
  });

  // Fetch public jobs and filter their run counts to only show runs from the
  // current project.
  // This prevents showing run counts from other projects using the same public jobs.
  const publicJobsQuery = useFetchProjectJobsQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      projectName: WANDB_JOBS_PROJECT_NAME,
      filters: createProjectIdFilter(
        projectInternalIdQuery.data?.project?.internalId ?? ''
      ),
    },
    skip:
      !hasPublicQueueAccess ||
      (entityName === WANDB_ENTITY_NAME &&
        projectName === WANDB_JOBS_PROJECT_NAME) ||
      projectInternalIdQuery.loading,
  });

  // Use the new incremental recently used jobs hook
  const recentlyUsedJobsResult = useRecentlyUsedJobs(
    entityName,
    projectName,
    createProjectIdFilter(
      projectInternalIdQuery.data?.project?.internalId ?? ''
    ),
    projectInternalIdQuery.loading
  );

  const projectJobsQuery = useFetchProjectJobsIncrementally(
    entityName,
    projectName
  );

  // Get data first to determine available tabs
  const publicJobEdges: any[] =
    publicJobsQuery.data?.project?.artifactType?.artifactCollections?.edges ??
    [];
  const jobEdges: any[] =
    projectJobsQuery.data?.project?.artifactType?.artifactCollections?.edges ??
    [];
  const projectJobs = getJobInfo(jobEdges, entityName, projectName);
  const publicJobs = getPublicJobInfo(
    publicJobEdges,
    WANDB_ENTITY_NAME,
    WANDB_JOBS_PROJECT_NAME
  );
  const recentlyUsedJobs = getRecentlyUsedJobInfo(
    recentlyUsedJobsResult.data?.recentlyUsedJobDetails ?? []
  );

  const hasPublicJobs = !!(hasPublicQueueAccess && publicJobs.length > 0);
  const hasProjectJobs = projectJobs.length > 0;
  const hasRecentlyUsedJobs = recentlyUsedJobs.length > 0;

  // set initial tab based on available options
  useEffect(() => {
    if (currentTab) {
      return;
    }

    if (hasProjectJobs) {
      setCurrentTab(JobsPageTab.PROJECT);
    } else if (hasPublicJobs) {
      setCurrentTab(JobsPageTab.PUBLIC);
    }
  }, [hasProjectJobs, hasPublicJobs, hasRecentlyUsedJobs, currentTab]);

  if (
    projectJobsQuery.loading ||
    publicJobsQuery.loading ||
    recentlyUsedJobsResult.loading ||
    isFeatureFlagLoading
  ) {
    return <Loader active name="jobs-page-loader" />;
  }

  if (!hasProjectJobs && !hasRecentlyUsedJobs) {
    // Note: BlankPage component does not match new UI style.
    return (
      <Tailwind>
        <div className="bg-white h-full px-16 py-30">
          <JobsHeader />
          <ZeroJobs />
        </div>
      </Tailwind>
    );
  }

  const onRun = (
    jobId: string,
    jobEntityName: string,
    jobProjectName: string
  ) => {
    setJobToRun({jobId, jobEntityName, jobProjectName});
  };
  const closeDrawer = () => {
    setJobToRun(null);
    Analytics.track('jobs drawer closed', {
      entityName,
    });
  };

  const onJobDelete = () => {
    projectJobsQuery.refetch();
  };

  const hasJobs = hasProjectJobs || hasPublicJobs || hasRecentlyUsedJobs;

  return (
    <Tailwind>
      <div className="bg-white h-full px-16 py-30">
        <JobsHeader />

        {/* Only show tabs if there are jobs in any category */}
        {currentTab && hasJobs && (
          <Tabs.Root
            value={currentTab}
            onValueChange={value => setCurrentTab(value as JobsPageTab)}>
            <Tabs.List>
              {hasProjectJobs && (
                <Tabs.Trigger value={JobsPageTab.PROJECT}>
                  {JOBS_PAGE_TAB_LABELS[JobsPageTab.PROJECT]}
                </Tabs.Trigger>
              )}
              {hasPublicJobs && (
                <Tabs.Trigger value={JobsPageTab.PUBLIC}>
                  {JOBS_PAGE_TAB_LABELS[JobsPageTab.PUBLIC]}
                </Tabs.Trigger>
              )}
            </Tabs.List>
            <div className="mt-16" />
            <Tabs.Content value={JobsPageTab.PROJECT}>
              {hasProjectJobs && (
                <JobsTable
                  jobs={projectJobs}
                  entityName={entityName}
                  projectName={projectName}
                  onRun={onRun}
                  onJobDelete={onJobDelete}
                />
              )}
            </Tabs.Content>
            <Tabs.Content value={JobsPageTab.PUBLIC}>
              {hasPublicJobs && (
                <Tailwind>
                  <InfoBanner
                    hintKey="public-jobs-coreweave"
                    icon="info"
                    bannerContent="Run reproducible W&B public jobs directly in your projects on a CoreWeave hardware queue."
                  />
                  <div className="mt-16">
                    <div className="grid grid-cols-1 gap-16 md:grid-cols-2 xl:grid-cols-3">
                      {publicJobs.map(job => (
                        <PublicJobCard
                          key={job.id}
                          job={job}
                          onLaunch={() =>
                            onRun(job.id, job.entityName, job.projectName)
                          }
                        />
                      ))}
                    </div>
                  </div>
                </Tailwind>
              )}
            </Tabs.Content>
          </Tabs.Root>
        )}

        <JobDrawer
          open={jobToRun != null}
          closeDrawer={closeDrawer}
          entityName={jobToRun?.jobEntityName ?? entityName}
          projectName={jobToRun?.jobProjectName ?? projectName}
          defaultJobId={jobToRun?.jobId ?? undefined}
          defaultJobVersion="latest"
        />
      </div>
    </Tailwind>
  );
};
